import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  AlertTriangle,
  CheckCircle2,
  MailCheck,
  RefreshCw,
  ShieldBan,
  XCircle,
} from "lucide-react";
import { getEmailDnsReport, getEmailSuppressionReport } from "@/lib/email-dns.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { APP_TIME_ZONE } from "@/lib/datetime";

export const Route = createFileRoute("/_authenticated/admin/email-dns")({
  head: () => ({
    meta: [
      { title: "Entregabilidade de e-mail · Admin" },
      {
        name: "description",
        content: "Validação automática de SPF, DKIM e DMARC do domínio remetente da plataforma.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: EmailDnsPage,
});

type Level = "ok" | "warning" | "error";

const LEVEL_LABEL: Record<Level, string> = {
  ok: "Conforme",
  warning: "Atenção",
  error: "Falha",
};

function LevelIcon({ level }: { level: Level }) {
  if (level === "ok") return <CheckCircle2 className="h-5 w-5 text-primary" aria-hidden />;
  if (level === "warning")
    return <AlertTriangle className="h-5 w-5 text-accent-foreground" aria-hidden />;
  return <XCircle className="h-5 w-5 text-destructive" aria-hidden />;
}

function LevelBadge({ level }: { level: Level }) {
  return (
    <Badge variant={level === "ok" ? "default" : level === "warning" ? "secondary" : "destructive"}>
      {LEVEL_LABEL[level]}
    </Badge>
  );
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      timeZone: APP_TIME_ZONE,
      dateStyle: "short",
      timeStyle: "medium",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatDay(iso: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", { timeZone: APP_TIME_ZONE, dateStyle: "short" }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

function SuppressionsCard() {
  const suppressionsFn = useServerFn(getEmailSuppressionReport);
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["email-suppressions"],
    queryFn: () => suppressionsFn(),
    refetchInterval: 1000 * 60 * 30,
  });

  return (
    <Card>
      <CardHeader className="gap-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldBan className="h-5 w-5 text-primary" aria-hidden />
            Supressões ativas
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} aria-hidden />
            Atualizar
          </Button>
        </div>
        <CardDescription>
          Endereços que a infraestrutura de e-mail está bloqueando, com o motivo e o prazo de
          liberação. Mensagens para estes destinatários são recusadas antes do envio.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Consultando registros de entrega…</p>
        ) : null}

        {data?.error ? (
          <Alert variant="destructive">
            <AlertTitle>Não foi possível consultar as supressões</AlertTitle>
            <AlertDescription>{data.error}</AlertDescription>
          </Alert>
        ) : null}

        {data && !data.error && data.entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhuma supressão ativa no período visível
            {data.historyStartsAt ? ` (desde ${formatDay(data.historyStartsAt)})` : ""}. Todos os
            destinatários estão aptos a receber e-mails.
          </p>
        ) : null}

        {data && !data.error && data.entries.length > 0 ? (
          <>
            <p className="text-sm text-muted-foreground">
              {data.entries.length} destinatário{data.entries.length > 1 ? "s" : ""} bloqueado
              {data.entries.length > 1 ? "s" : ""} · {data.blockedAttempts} tentativa
              {data.blockedAttempts === 1 ? "" : "s"} recusada
              {data.blockedAttempts === 1 ? "" : "s"}
              {data.historyStartsAt ? ` desde ${formatDay(data.historyStartsAt)}` : ""}.
            </p>
            <ul className="space-y-3">
              {data.entries.map((entry) => {
                const days = entry.expiresAt ? daysUntil(entry.expiresAt) : null;
                return (
                  <li
                    key={entry.recipient}
                    className="rounded-lg border border-border bg-card/50 p-4 space-y-2"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="break-all font-medium">{entry.recipient}</span>
                      <Badge variant={entry.reversibleByRecipient ? "secondary" : "destructive"}>
                        {entry.reason}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{entry.guidance}</p>
                    <dl className="grid gap-x-6 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2">
                      <div className="flex gap-1">
                        <dt className="font-medium">Bloqueado desde:</dt>
                        <dd>{formatDay(entry.firstAt)}</dd>
                      </div>
                      <div className="flex gap-1">
                        <dt className="font-medium">Última ocorrência:</dt>
                        <dd>{formatDate(entry.lastAt)}</dd>
                      </div>
                      <div className="flex gap-1">
                        <dt className="font-medium">Expira em:</dt>
                        <dd>
                          {entry.expiresAt
                            ? `${formatDay(entry.expiresAt)}${
                                days !== null && days > 0
                                  ? ` (em ${days} dia${days > 1 ? "s" : ""})`
                                  : ""
                              }${entry.expiresEstimated ? " · estimativa" : ""}${
                                entry.historyClamped ? " (pode ser antes)" : ""
                              }`
                            : entry.reversibleByRecipient
                              ? "Sem prazo — depende do destinatário"
                              : "Sem prazo definido"}
                        </dd>
                      </div>
                      <div className="flex gap-1">
                        <dt className="font-medium">Tentativas recusadas:</dt>
                        <dd>{entry.blockedAttempts}</dd>
                      </div>
                    </dl>
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-muted-foreground">
              As supressões são aplicadas pela infraestrutura de envio e não podem ser removidas
              pela plataforma. Prazos marcados como estimativa são calculados a partir da última
              ocorrência registrada; quando o histórico visível não alcança o bloqueio original, a
              liberação pode acontecer antes da data indicada.
            </p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

function EmailDnsPage() {
  const reportFn = useServerFn(getEmailDnsReport);
  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["email-dns-report"],
    queryFn: () => reportFn(),
    refetchInterval: 1000 * 60 * 30,
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <MailCheck className="h-6 w-6 text-primary" aria-hidden />
            Entregabilidade de e-mail
          </h1>
          <p className="text-sm text-muted-foreground">
            Verificação automática de SPF, DKIM, DMARC, retornos e delegação do domínio remetente.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isFetching}
          className="sm:w-auto"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} aria-hidden />
          Verificar agora
        </Button>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Não foi possível validar os registros</AlertTitle>
          <AlertDescription>{String((error as any)?.message ?? error)}</AlertDescription>
        </Alert>
      ) : null}

      {isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Consultando os servidores de DNS…
          </CardContent>
        </Card>
      ) : null}

      {data ? (
        <>
          <Card>
            <CardHeader className="gap-2">
              <div className="flex flex-wrap items-center gap-3">
                <LevelIcon level={data.level as Level} />
                <CardTitle className="text-lg">{data.domain}</CardTitle>
                <LevelBadge level={data.level as Level} />
              </div>
              <CardDescription>
                {data.summary} · Última verificação em {formatDate(data.checkedAt)}
              </CardDescription>
            </CardHeader>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            {data.records.map((record) => (
              <Card key={record.key}>
                <CardHeader className="gap-2 pb-3">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <LevelIcon level={record.level as Level} />
                      {record.name}
                    </CardTitle>
                    <LevelBadge level={record.level as Level} />
                  </div>
                  <CardDescription>{record.detail}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {record.value ? (
                    <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs leading-relaxed">
                      {record.value}
                    </pre>
                  ) : null}
                  {record.hint ? (
                    <p className="text-xs text-muted-foreground">
                      <span className="font-medium">Como corrigir:</span> {record.hint}
                    </p>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      ) : null}

      <SuppressionsCard />
    </div>
  );
}
